import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

export const ICON = { size: 18, strokeWidth: 1.75 } as const;

export function Logo({ className = "auth-logo", white = false }: { className?: string; white?: boolean }) {
  return (
    <img
      className={className}
      src={white ? "/brand/meniova-logo-white.svg" : "/brand/meniova-logo.svg"}
      alt="Meniova"
    />
  );
}

type Tone = "error" | "success" | "info" | "warning";
const TONE_ICON = { error: CircleAlert, success: CircleCheck, info: Info, warning: TriangleAlert };

export function Alert({
  tone,
  title,
  children,
  live = false,
}: {
  tone: Tone;
  title?: string;
  children?: ReactNode;
  live?: boolean;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <div className={`alert alert-${tone}`} role={live ? (tone === "error" ? "alert" : "status") : undefined}>
      <Icon {...ICON} aria-hidden="true" />
      <div>
        {title && <div className="alert-title">{title}</div>}
        {children && <div className="alert-body">{children}</div>}
      </div>
    </div>
  );
}

interface FieldBase {
  id: string;
  label: string;
  hint?: string;
  help?: string;
  error?: string | undefined;
}

function describedBy(id: string, help?: string, error?: string) {
  return [help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
}

function FieldMeta({ id, help, error }: { id: string; help?: string; error?: string | undefined }) {
  return (
    <>
      {help && (
        <div id={`${id}-help`} className="field-help">
          {help}
        </div>
      )}
      {error && (
        <div id={`${id}-error`} className="field-error">
          <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </>
  );
}

export function TextField({
  id,
  label,
  hint,
  help,
  error,
  ...input
}: FieldBase & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label} {hint && <span className="field-hint">{hint}</span>}
      </label>
      <input
        id={id}
        className="field-input"
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy(id, help, error)}
        {...input}
      />
      <FieldMeta id={id} help={help} error={error} />
    </div>
  );
}

export function SelectField({
  id,
  label,
  hint,
  help,
  error,
  options,
  ...select
}: FieldBase &
  SelectHTMLAttributes<HTMLSelectElement> & { options: ReadonlyArray<readonly [string, string]> }) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label} {hint && <span className="field-hint">{hint}</span>}
      </label>
      <select
        id={id}
        className="field-input"
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy(id, help, error)}
        {...select}
      >
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
      <FieldMeta id={id} help={help} error={error} />
    </div>
  );
}

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <Logo />
      <main className="auth-card">{children}</main>
      <p className="auth-tagline">Your menu. Made simple.</p>
    </div>
  );
}

/** Moves focus to the first invalid field after a failed submit. */
export function focusFirstInvalid(form: HTMLFormElement | null) {
  setTimeout(() => form?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
}
